//
// Copyright (c) 2025 Comcast
//

import { useState, useEffect, useCallback } from 'react'
import { getFieldParamsByJsonSchemaEx } from './utils'

// after trigger field is changed, reset dependent fields
// and return new form values
const resetDependentFormFields = (form, field) => {
  // reset dependent dynamic props
  if (field.dependentFieldParams) {
    field.dependentFieldParams.forEach(depField => {
      // we need to reset to 'undefined', otherwise property remains
      // in the result object after changing value of the trigger prop
      // from 1) the option when dependent field existed
      // to 2) the option when dependent field disappeared
      form.change(depField.fullName, undefined)
    })
  }

  // reset dependent dynamic groups
  if (field.dependentFieldGroups) {
    field.dependentFieldGroups.forEach(group => {
      // set to undefined so that it will be deleted
      form.change(`${group.groupPrefix}.${group.group}`, undefined)
    })
  }

  const newFormValues = form.getState().values
  return newFormValues
}

// Hook for processing forms with dependent fields
export const useFormWithDeps = (jsonSchemaEx, initialValues, visible) => {
  const [formState, setFormState] = useState({
    fieldParams: null,
    tabs: null,
    filteredInitialValues: null,
  })

  const onTriggerPropChange = useCallback((value, previous, form, field) => {
    const newFormValues = resetDependentFormFields(form, field)
    const { fieldParams } = getFieldParamsByJsonSchemaEx(
      jsonSchemaEx,
      newFormValues,
      onTriggerPropChange,
    )
    setFormState(prev => ({ ...prev, fieldParams }))
  }, [jsonSchemaEx])

  useEffect(() => {
    if (!visible) {
      return
    }

    const { fieldParams, tabs, filteredInitialValues } = getFieldParamsByJsonSchemaEx(
      jsonSchemaEx,
      initialValues,
      onTriggerPropChange,
    )
    setFormState({
      fieldParams: fieldParams,
      tabs: tabs,
      filteredInitialValues: filteredInitialValues,
    })
  // adding initialValues here will start infinite reloading if initialValues
  // is not memoized in component. We don't need this dependency because forms
  // are modal and initialValues cannot change. If it changes (for page forms), we'll need update here.
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  return formState
}
